import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import type { CompetenceObservationWithLevelDto } from '@/composables/competenceObservation/competenceObservation.types';
import { FilterOperationType, type IFilter } from '@chax-at/prisma-filter-common';
import type { ICompetenceObservationFilter } from 'common/interface/filter.interface';
import { toIsoDateTimeString } from 'common/utils/date.utils';

export async function fetchCompetenceObservations(params: {
  classId: number;
}): Promise<Components.Schemas.CompetenceObservationDto[]> {
  const filter: IFilter<ICompetenceObservationFilter> = {
    filter: [{ field: 'schoolClassId', type: FilterOperationType.Eq, value: params.classId }],
    order: [{ field: 'observedAt', dir: 'desc' }],
  };

  const res = await Api.Routes.CompetenceObservation.retrieve({ $filter: filter });
  return res.rows;
}

export async function fetchCompetenceObservationsStudent(params: {
  classId: number;
  studentId: number;
  competenceId?: number;
  competenceIds?: readonly number[];
  from?: string;
  to?: string;
}): Promise<Components.Schemas.CompetenceObservationDto[]> {
  if (params.competenceIds?.length === 0) return [];

  const filter = buildStudentCompetenceObservationFilter(params);
  const res = await Api.Routes.CompetenceObservation.retrieve({ $filter: filter });
  return res.rows;
}

export async function fetchCompetenceObservationsStudentRated(params: {
  classId: number;
  studentId: number;
  competenceId?: number;
}): Promise<CompetenceObservationWithLevelDto[]> {
  const filter = buildStudentCompetenceObservationFilter(params, true);
  const res = await Api.Routes.CompetenceObservation.retrieve({ $filter: filter });
  return res.rows as CompetenceObservationWithLevelDto[];
}

export const buildStudentCompetenceObservationFilter = (
  params: {
    classId: number;
    competenceId?: number;
    competenceIds?: readonly number[];
    from?: string;
    studentId: number;
    to?: string;
  },
  ratedOnly = false,
): IFilter<ICompetenceObservationFilter> => {
  const filter: IFilter<ICompetenceObservationFilter> = {
    filter: [
      { field: 'schoolClassId', type: FilterOperationType.Eq, value: params.classId },
      { field: 'studentId', type: FilterOperationType.Eq, value: params.studentId },
      ...(ratedOnly ? [{ field: 'performanceLevelV2Id', type: FilterOperationType.Ne, value: null } as const] : []),
    ],
    order: [{ field: 'observedAt', dir: 'desc' }],
  };

  if (params.competenceId != null) {
    filter.filter?.push({
      field: 'competenceId',
      type: FilterOperationType.Eq,
      value: params.competenceId,
    });
  }

  if (params.competenceIds) {
    filter.filter?.push({
      field: 'competenceId',
      type: FilterOperationType.In,
      value: [...params.competenceIds],
    });
  }

  if (params.from) {
    filter.filter?.push({
      field: 'observedAt',
      type: FilterOperationType.Gte,
      value: toIsoDateTimeString(params.from),
    });
  }

  if (params.to) {
    filter.filter?.push({
      field: 'observedAt',
      type: FilterOperationType.Lte,
      value: toIsoDateTimeString(params.to),
    });
  }

  return filter;
};

export async function updateCompetenceObservation(params: {
  observationId: number;
  dto: Components.Schemas.UpdateCompetenceObservationDto;
}): Promise<void> {
  await Api.Routes.CompetenceObservation.update({ observationId: params.observationId }, params.dto);
}

export async function bulkUpdateCompetenceObservationLevel(
  dto: Components.Schemas.BulkUpdateCompetenceObservationLevelDto,
): Promise<void> {
  await Api.Routes.CompetenceObservation.bulkUpdateLevel(null, dto);
}
